'use client';

import { useMemo, useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@/lib/table-features';
import {
  parseAsArrayOf,
  parseAsInteger,
  parseAsString,
  useQueryState
} from 'nuqs';
import type { ExtendedColumnSort } from '@/types/data-table';
import type { BulkPayload } from '@/types/bulk-action';
import { getSortingStateParser } from '@/lib/parsers';
import {
  Building,
  Loader2,
  MoreHorizontal,
  Pencil,
  Plus,
  Eye,
  Trash2,
  Users
} from 'lucide-react';

import { useDataTable } from '@/hooks/use-data-table';
import { useFilteredColumns } from '@/hooks/use-filtered-columns';
import { useSelectAllPages } from '@/hooks/use-select-all-pages';
import { useHashDialog, useHashParamDialog } from '@/hooks/use-hash-dialog';
import { cn } from '@/lib/utils';
import { getImageUrl } from '@/lib/image';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { ListingSkeleton } from '@/components/listing-skeleton';
import { MetricsSkeleton } from '@/components/metrics-skeleton';
import { DataTable } from '@/components/ui/table/data-table';
import { DataTableToolbar } from '@/components/ui/table/data-table-toolbar';
import { DataTableColumnHeader } from '@/components/ui/table/data-table-column-header';
import { DataViewToggle, useDataView } from '@/components/data-view-toggle';
import { PaginationControls } from '@/components/pagination-controls';
import { SelectAllBanner } from '@/components/select-all-banner';
import { SortControl } from '@/components/sort-control';
import {
  HighlightMatch,
  getColumnSearchTerms
} from '@/components/highlight-match';
import { PermissionGate } from '@/features/auth';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { CLIENTS_HASH } from '../constants';
import { clientQueries } from '../services/clients.queries';
import { useDeleteClientMutation } from '../services/clients.mutations';
import { ClientFormDialog } from './client-form-dialog';
import { ClientDetailDialog } from './client-detail-dialog';
import { ClientsTableActionBar } from './clients-table-action-bar';
import { ClientCard } from './client-card';
import { ClientCompactRow } from './client-compact-row';
import { formatLastLogin } from '../mappers/clients.mapper';
import type { Client, ClientsListParams, ClientStatus } from '../types';

const STATUS_STYLES: Record<string, string> = {
  active: 'border-sky-500/30 bg-sky-500/10 text-sky-600 dark:text-sky-400',
  inactive: 'border-gray-500/30 bg-gray-500/10 text-gray-600 dark:text-gray-400'
};

export function ClientListing() {
  const t = useTranslations('clients');
  // Overlay open-state lives in the URL hash (#create, #edit/<id>, #view/<id>,
  // #delete/<id>) so overlays are deep-linkable and close on browser Back.
  // Destructure the stable callbacks so they can go in hook dep arrays without
  // tripping the React Compiler's manual-memoization check.
  const { open: createOpen, onOpenChange: toggleCreate } = useHashDialog(
    CLIENTS_HASH.create
  );
  const {
    open: editOpen,
    param: editId,
    openWith: openEdit,
    onOpenChange: closeEdit
  } = useHashParamDialog(CLIENTS_HASH.edit);
  const {
    open: viewOpen,
    param: viewId,
    openWith: openView,
    onOpenChange: closeView
  } = useHashParamDialog(CLIENTS_HASH.view);
  const {
    open: deleteOpen,
    param: deleteId,
    openWith: openDelete,
    onOpenChange: closeDelete
  } = useHashParamDialog(CLIENTS_HASH.delete);
  const [view, setView] = useDataView('clients');

  const deleteMutation = useDeleteClientMutation();

  // URL state
  const [urlPage] = useQueryState('page', parseAsInteger.withDefault(1));
  const [urlPerPage] = useQueryState('perPage', parseAsInteger.withDefault(10));
  const [urlSearch] = useQueryState('name', parseAsString.withDefault(''));
  const [urlStatus] = useQueryState(
    'status',
    parseAsArrayOf(parseAsString, ',').withDefault([])
  );
  const [urlSorting] = useQueryState(
    'sort',
    getSortingStateParser<Client>().withDefault([
      { id: 'created_at', desc: true }
    ] as ExtendedColumnSort<Client>[])
  );

  const queryParams = useMemo<ClientsListParams>(() => {
    const params: ClientsListParams = {
      page: urlPage,
      per_page: urlPerPage
    };
    if (urlSearch) {
      params.search = urlSearch;
    }
    if (urlStatus.length) {
      params.status = urlStatus as ClientStatus[];
    }
    params.sort_by = urlSorting[0]?.id ?? 'name';
    params.sort_direction = urlSorting[0]?.desc ? 'desc' : 'asc';
    return params;
  }, [urlPage, urlPerPage, urlSearch, urlStatus, urlSorting]);

  const { data, isPending, isPlaceholderData } = useQuery(
    clientQueries.list(queryParams)
  );

  const { data: metricsData, isPending: metricsLoading } = useQuery(
    clientQueries.metrics()
  );

  const clients = useMemo(() => (data?.data ?? []) as Client[], [data]);
  const pageCount = data?.pagination?.totalPages ?? 0;
  const pagination = data?.pagination;
  const metrics = metricsData?.data;

  // Resolve the hash id param back to the loaded row (row must be on the
  // current page; deep-linking an off-page id simply won't open the overlay).
  const editTarget = editOpen
    ? (clients.find((c) => String(c.id) === editId) ?? null)
    : null;
  const detailTarget = viewOpen
    ? (clients.find((c) => String(c.id) === viewId) ?? null)
    : null;
  const deleteTarget = deleteOpen
    ? (clients.find((c) => String(c.id) === deleteId) ?? null)
    : null;

  const columns = useMemo<ColumnDef<Client>[]>(
    () => [
      {
        id: 'select',
        header: ({ table }) => (
          <Checkbox
            checked={
              table.getIsAllPageRowsSelected() ||
              (table.getIsSomePageRowsSelected() && 'indeterminate')
            }
            onCheckedChange={(value) =>
              table.toggleAllPageRowsSelected(!!value)
            }
            aria-label='Select all'
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label='Select row'
          />
        ),
        enableSorting: false,
        enableHiding: false,
        size: 40
      },
      {
        id: 'name',
        accessorKey: 'name',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('name')} />
        ),
        cell: ({ row, column }) => (
          <div className='flex items-center gap-3'>
            <Avatar className='h-10 w-10 shrink-0 rounded-md'>
              {row.original.logo_url && (
                <AvatarImage
                  src={getImageUrl(row.original.logo_url)}
                  alt={row.original.name}
                  className='object-cover'
                />
              )}
              <AvatarFallback className='rounded-md text-sm font-bold'>
                {row.original.name.charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div>
              <span className='font-medium'>
                <HighlightMatch
                  text={row.original.name}
                  query={getColumnSearchTerms(column)}
                />
              </span>
            </div>
          </div>
        ),
        enableColumnFilter: true,
        meta: {
          label: t('searchPlaceholder'),
          variant: 'text' as const,
          placeholder: t('searchPlaceholder'),
          permission: 'clients.column.name'
        }
      },
      {
        id: 'client_id',
        accessorKey: 'client_id',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('clientId')} />
        ),
        cell: ({ row }) => (
          <span className='text-muted-foreground font-mono text-sm'>
            {row.original.client_id}
          </span>
        ),
        meta: { label: t('clientId') }
      },
      {
        id: 'admin_email',
        accessorKey: 'admin_email',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('adminEmail')} />
        ),
        cell: ({ row }) => (
          <a
            href={`mailto:${row.original.admin_email}`}
            className='text-muted-foreground hover:underline'
          >
            {row.original.admin_email}
          </a>
        ),
        meta: {
          label: t('adminEmail'),
          permission: 'clients.column.adminEmail'
        }
      },
      {
        id: 'last_login_at',
        accessorKey: 'last_login_at',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('lastLogin')} />
        ),
        cell: ({ row }) => (
          <span className='text-muted-foreground text-sm'>
            {formatLastLogin(row.original.last_login_at) ?? t('never')}
          </span>
        ),
        meta: {
          label: t('lastLogin'),
          permission: 'clients.column.lastLogin'
        }
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('status')} />
        ),
        cell: ({ row }) => (
          <Badge
            variant='outline'
            className={cn(
              'text-xs uppercase',
              STATUS_STYLES[row.original.status]
            )}
          >
            {row.original.status === 'active'
              ? t('statusActive')
              : t('statusInactive')}
          </Badge>
        ),
        enableColumnFilter: true,
        meta: {
          label: t('status'),
          variant: 'multiSelect' as const,
          icon: 'solar:tag-bold-duotone',
          options: [
            { value: 'active', label: t('statusActive') },
            { value: 'inactive', label: t('statusInactive') }
          ],
          permission: 'clients.column.status'
        }
      },
      {
        id: 'actions',
        header: () => <div className='text-end'>{t('actions')}</div>,
        cell: ({ row }) => {
          const client = row.original;
          return (
            <div className='flex justify-end'>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant='ghost' size='icon' className='h-8 w-8'>
                    <MoreHorizontal className='h-4 w-4' />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align='end'>
                  <PermissionGate permissions={['clients.edit']}>
                    <DropdownMenuItem
                      onClick={() => openEdit(String(client.id))}
                    >
                      <Pencil className='me-2 h-4 w-4' />
                      {t('edit')}
                    </DropdownMenuItem>
                  </PermissionGate>
                  <DropdownMenuItem onClick={() => openView(String(client.id))}>
                    <Eye className='me-2 h-4 w-4' />
                    {t('viewDetails')}
                  </DropdownMenuItem>
                  <PermissionGate permissions={['clients.delete']}>
                    <DropdownMenuItem
                      className='text-destructive'
                      onClick={() => openDelete(String(client.id))}
                    >
                      <Trash2 className='me-2 h-4 w-4' />
                      {t('delete')}
                    </DropdownMenuItem>
                  </PermissionGate>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        },
        enableSorting: false,
        enableHiding: false
      }
    ],
    [t, openEdit, openView, openDelete]
  );

  const filteredColumns = useFilteredColumns(columns);

  const { table } = useDataTable<Client>({
    columns: filteredColumns,
    pageKey: 'clients',
    data: clients,
    pageCount,
    initialState: {
      sorting: [{ id: 'created_at', desc: true }],
      pagination: { pageIndex: 0, pageSize: 10 }
    }
  });

  const selectAll = useSelectAllPages(table, pagination?.total ?? 0);

  const getBulkPayload = useCallback((): BulkPayload => {
    if (selectAll.isAllPagesSelected) {
      const { page, per_page, sort_by, sort_direction, ...filters } =
        queryParams;
      return { mode: 'all', filters };
    }
    const selectedIds = table
      .getFilteredSelectedRowModel()
      .rows.map((row) => row.original.id);
    return { mode: 'ids', ids: selectedIds };
  }, [selectAll.isAllPagesSelected, queryParams, table]);

  const handleBulkComplete = useCallback(() => {
    selectAll.reset();
  }, [selectAll]);

  const handleEdit = useCallback(
    (client: Client) => openEdit(String(client.id)),
    [openEdit]
  );

  const handleDelete = useCallback(
    (client: Client) => openDelete(String(client.id)),
    [openDelete]
  );

  async function confirmDelete() {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
    } catch {
      // handled by global MutationCache
    }
    closeDelete(false);
  }

  const tableState = table.state;

  const sortOptions = useMemo(
    () => [
      { label: t('name'), value: 'name' },
      { label: t('clientId'), value: 'client_id' },
      { label: t('adminEmail'), value: 'admin_email' },
      { label: t('lastLogin'), value: 'last_login_at' },
      { label: t('createdAt'), value: 'created_at' }
    ],
    [t]
  );

  return (
    <div className='space-y-6'>
      {/* Metrics Cards */}
      {metricsLoading ? (
        <MetricsSkeleton />
      ) : metrics ? (
        <div className='grid gap-4 sm:grid-cols-2 lg:grid-cols-3'>
          <Card>
            <CardContent className='flex items-center gap-4 p-6'>
              <div className='bg-primary/10 flex h-12 w-12 items-center justify-center rounded-lg'>
                <Building className='text-primary h-6 w-6' />
              </div>
              <div>
                <p className='text-muted-foreground text-sm'>
                  {t('totalClients')}
                </p>
                <p className='text-2xl font-bold'>{metrics.total_clients}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className='flex items-center gap-4 p-6'>
              <div className='flex h-12 w-12 items-center justify-center rounded-lg bg-sky-500/10'>
                <Users className='h-6 w-6 text-sky-500' />
              </div>
              <div>
                <p className='text-muted-foreground text-sm'>
                  {t('activeClients')}
                </p>
                <p className='text-2xl font-bold'>{metrics.active_count}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className='flex items-center gap-4 p-6'>
              <div className='flex h-12 w-12 items-center justify-center rounded-lg bg-gray-500/10'>
                <Users className='h-6 w-6 text-gray-500' />
              </div>
              <div>
                <p className='text-muted-foreground text-sm'>
                  {t('inactiveClients')}
                </p>
                <p className='text-2xl font-bold'>{metrics.inactive_count}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      ) : null}

      {/* Toolbar: View Toggle + Add */}
      <div className='flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-end'>
        <div className='flex items-center gap-2'>
          {view !== 'table' && (
            <SortControl
              options={sortOptions}
              sorting={tableState.sorting}
              onSortingChange={(s) => table.setSorting(s)}
            />
          )}
          <DataViewToggle
            value={view}
            onValueChange={setView}
            pageKey='clients'
          />
          <PermissionGate permissions={['clients.create']}>
            <Button onClick={() => toggleCreate(true)}>
              <Plus className='me-2 h-4 w-4' />
              {t('addClient')}
            </Button>
          </PermissionGate>
        </div>
      </div>

      <DataTableToolbar
        table={table}
        viewFilterPermission='clients.view_filter'
      />

      {/* Data */}
      {isPending ? (
        <ListingSkeleton view={view} />
      ) : clients.length === 0 && tableState.columnFilters.length === 0 ? (
        <div className='flex flex-col items-center justify-center py-12 text-center'>
          <Building className='text-muted-foreground mb-4 h-12 w-12' />
          <p className='text-muted-foreground text-lg'>{t('noClients')}</p>
        </div>
      ) : view === 'grid' ? (
        <>
          <div
            className={cn(
              'grid gap-4 sm:grid-cols-2 lg:grid-cols-3',
              isPlaceholderData && 'opacity-60 transition-opacity'
            )}
          >
            {clients.map((client) => (
              <ClientCard
                key={client.id}
                client={client}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ))}
          </div>
          {pagination && pagination.totalPages > 0 && (
            <PaginationControls
              page={pagination.page}
              pageCount={pagination.totalPages}
              perPage={pagination.per_page}
              total={pagination.total}
              onPageChange={(p) => table.setPageIndex(p - 1)}
              onPerPageChange={(s) => {
                table.setPageSize(s);
                table.setPageIndex(0);
              }}
            />
          )}
        </>
      ) : view === 'compact' ? (
        <>
          <div
            className={cn(
              'space-y-2',
              isPlaceholderData && 'opacity-60 transition-opacity'
            )}
          >
            {clients.map((client) => (
              <ClientCompactRow
                key={client.id}
                client={client}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ))}
          </div>
          {pagination && pagination.totalPages > 0 && (
            <PaginationControls
              page={pagination.page}
              pageCount={pagination.totalPages}
              perPage={pagination.per_page}
              total={pagination.total}
              onPageChange={(p) => table.setPageIndex(p - 1)}
              onPerPageChange={(s) => {
                table.setPageSize(s);
                table.setPageIndex(0);
              }}
            />
          )}
        </>
      ) : (
        <div
          className={cn(
            'flex min-h-[500px] flex-col gap-2',
            isPlaceholderData && 'opacity-60 transition-opacity'
          )}
        >
          <SelectAllBanner
            isAllPagesSelected={selectAll.isAllPagesSelected}
            isCurrentPageFullySelected={selectAll.isCurrentPageFullySelected}
            currentPageCount={clients.length}
            totalCount={pagination?.total ?? 0}
            pageSize={tableState.pagination.pageSize}
            onSelectAllPages={() => selectAll.setAllPagesSelected(true)}
            onClearSelection={() => selectAll.reset()}
          />
          <DataTable
            table={table}
            actionBar={
              <ClientsTableActionBar
                table={table}
                getBulkPayload={getBulkPayload}
                onComplete={handleBulkComplete}
                isAllPagesSelected={selectAll.isAllPagesSelected}
                totalCount={pagination?.total ?? 0}
              />
            }
          ></DataTable>
        </div>
      )}

      {/* Form dialog — #create / #edit/<id> */}
      <ClientFormDialog
        open={createOpen || (editOpen && !!editTarget)}
        onOpenChange={(open) => {
          if (!open) (editOpen ? closeEdit : toggleCreate)(false);
        }}
        client={editOpen ? editTarget : null}
      />

      {/* Detail dialog — #view/<id> */}
      <ClientDetailDialog
        open={viewOpen && !!detailTarget}
        onOpenChange={closeView}
        client={detailTarget}
      />

      {/* Delete confirmation — #delete/<id> */}
      <AlertDialog
        open={deleteOpen && !!deleteTarget}
        onOpenChange={closeDelete}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('deleteConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('deleteConfirmDescription', {
                name: deleteTarget?.name ?? ''
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className='bg-destructive text-destructive-foreground hover:bg-destructive/90'
            >
              {deleteMutation.isPending && (
                <Loader2 className='me-2 h-4 w-4 animate-spin' />
              )}
              {t('confirmDelete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
